import React from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeroSection = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  overflow: hidden;
  padding-top: 4rem;
`;

const HeroPattern = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-image: radial-gradient(rgba(37, 99, 235, 0.12) 1px, transparent 0);
  background-size: 34px 34px;
  opacity: 0.5;
  z-index: 0;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 6rem 2rem 2rem;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    padding-top: 8rem;
  }
`;

const Greeting = styled.div`
  font-size: 1.2rem;
  color: var(--secondary-color);
  margin-bottom: 1rem;
  font-family: var(--mono-font);
`;

const LocationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background-color: var(--white);
  border: 1px solid var(--border-color);
  color: var(--dark-gray);
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  margin-bottom: 1.5rem;
  box-shadow: var(--shadow-sm);
`;

const AvailabilityDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #16a34a;
  display: inline-block;
  box-shadow: 0 0 0 2px #dcfce7;
`;

const Title = styled.h1`
  font-size: 3.75rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--primary-color);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: var(--dark-gray);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Description = styled.p`
  font-size: 1.05rem;
  max-width: 600px;
  margin-bottom: 2rem;
  color: var(--dark-gray);
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PrimaryButton = styled.a`
  padding: 0.8rem 1.3rem;
  background-color: var(--secondary-color);
  color: white;
  border-radius: 999px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-block;
  text-decoration: none;
  
  &:hover {
    background-color: var(--primary-color);
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.a`
  padding: 0.8rem 1.3rem;
  background-color: transparent;
  color: var(--secondary-color);
  border: 1px solid var(--secondary-color);
  border-radius: 999px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-block;
  text-decoration: none;
  
  &:hover {
    background-color: var(--secondary-color);
    color: white;
    transform: translateY(-2px);
  }
`;

const Hero = () => {
  return (
    <HeroSection>
      <HeroPattern />
      <HeroContent>
        <LocationBadge>
          <AvailabilityDot /> Dubai, UAE &nbsp;·&nbsp; Open to opportunities
        </LocationBadge>
        <Greeting>Hello, my name is</Greeting>
        <Title>Sultan Malik</Title>
        <Subtitle>Java Fullstack Engineer</Subtitle>
        <Description>
          I build reliable APIs and distributed systems with Java, Spring, and cloud-native tooling.
          Focused on clean architecture, observability, and performance at scale.
        </Description>
        <ButtonsContainer>
          <PrimaryButton href="#projects">View My Work</PrimaryButton>
          <SecondaryButton href="/resume.pdf" target="_blank" rel="noopener noreferrer">Download Resume</SecondaryButton>
        </ButtonsContainer>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero;
